'use client';
import React, { useState, useMemo } from 'react';
import { Search, SlidersHorizontal, Star, MapPin, Award, Globe, Heart, MessageSquare, X } from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import ProductDetailModal from './ProductDetailModal';

const categories = [
  'All Categories', 'Halal Food & Beverage', 'Cosmetics', 'Pharmaceuticals',
  'Modest Fashion', 'Agriculture', 'Ingredients', 'Packaging', 'Logistics', 'Technology Services',
];

const countries = ['All Countries', 'Malaysia', 'China', 'United States', 'Indonesia', 'UAE', 'Saudi Arabia', 'Türkiye', 'Singapore'];

const certStatuses = ['All Status', 'Valid', 'Expiring Soon', 'Under Review'];

export const products = [
  {
    id: 'prod-001',
    name: 'Premium Halal Frozen Beef',
    supplier: 'Nusantara Halal Foods Sdn Bhd',
    country: 'Malaysia',
    flag: '🇲🇾',
    category: 'Halal Food & Beverage',
    certStatus: 'Valid',
    certBody: 'JAKIM Recognized Partner',
    certId: 'HC-MY-2026-004582',
    trustScore: 94,
    exportReady: ['China', 'UAE', 'Saudi Arabia'],
    moq: '500 kg',
    description: 'Premium quality halal-certified frozen beef sourced from grass-fed cattle. Compliant with JAKIM, ESMA, and MUI standards.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-002',
    name: 'Halal Certified Skincare Range',
    supplier: 'PureEthic Beauty LLC',
    country: 'United States',
    flag: '🇺🇸',
    category: 'Cosmetics',
    certStatus: 'Valid',
    certBody: 'IFANCA',
    certId: 'HC-US-2026-001129',
    trustScore: 91,
    exportReady: ['Malaysia', 'Indonesia', 'GCC'],
    moq: '200 units',
    description: 'Cruelty-free, alcohol-free halal skincare range certified by IFANCA. Suitable for Muslim-majority and ethical consumer markets.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-003',
    name: 'Halal Food Ingredients',
    supplier: 'Zhejiang Halal Ingredients Co.',
    country: 'China',
    flag: '🇨🇳',
    category: 'Ingredients',
    certStatus: 'Under Review',
    certBody: 'China Halal Certification Partner',
    certId: 'HC-CN-2026-008921',
    trustScore: 78,
    exportReady: ['Malaysia', 'Indonesia'],
    moq: '1,000 kg',
    description: 'Wide range of halal-certified food ingredients including emulsifiers, stabilizers, and flavor compounds for halal food manufacturers.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-004',
    name: 'Ready-to-Eat Halal Meals',
    supplier: 'Global Tayyib Foods',
    country: 'Malaysia',
    flag: '🇲🇾',
    category: 'Halal Food & Beverage',
    certStatus: 'Valid',
    certBody: 'JAKIM',
    certId: 'HC-MY-2026-006814',
    trustScore: 96,
    exportReady: ['US', 'China', 'OIC Markets'],
    moq: '1,000 packs',
    description: 'Individually packaged ready-to-eat halal meals for retail, airline catering, and institutional supply. Shelf life 18 months.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-005',
    name: 'Organic Halal Honey',
    supplier: 'Al-Rawdah Apiaries',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    category: 'Halal Food & Beverage',
    certStatus: 'Valid',
    certBody: 'SASO Recognized',
    certId: 'HC-SA-2026-003341',
    trustScore: 97,
    exportReady: ['Malaysia', 'Indonesia', 'UAE', 'UK'],
    moq: '100 kg',
    description: 'Premium Sidr and Acacia honey from certified organic apiaries. Compliant with GCC halal food standards.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-006',
    name: 'Halal Pharmaceutical Excipients',
    supplier: 'PharmaTayyib Indonesia',
    country: 'Indonesia',
    flag: '🇮🇩',
    category: 'Pharmaceuticals',
    certStatus: 'Valid',
    certBody: 'MUI Certified',
    certId: 'HC-ID-2026-007723',
    trustScore: 89,
    exportReady: ['Malaysia', 'GCC', 'OIC Markets'],
    moq: '500 kg',
    description: 'MUI-certified pharmaceutical excipients including gelatin alternatives, coatings, and binders for halal drug formulations.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-007',
    name: 'Modest Fashion Collection',
    supplier: 'Layali Modest Wear',
    country: 'Türkiye',
    flag: '🇹🇷',
    category: 'Modest Fashion',
    certStatus: 'Valid',
    certBody: 'TSE Halal Certified',
    certId: 'HC-TR-2026-002198',
    trustScore: 85,
    exportReady: ['Malaysia', 'Indonesia', 'UAE', 'UK'],
    moq: '50 pieces',
    description: 'Premium modest fashion collection including abayas, hijabs, and modest sportswear. Ethically sourced and halal-certified materials.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-008',
    name: 'Halal Logistics & Cold Chain',
    supplier: 'TrustFreight Asia',
    country: 'Singapore',
    flag: '🇸🇬',
    category: 'Logistics',
    certStatus: 'Valid',
    certBody: 'MUIS Certified',
    certId: 'HC-SG-2026-001044',
    trustScore: 93,
    exportReady: ['Malaysia', 'China', 'Indonesia', 'GCC'],
    moq: 'Per shipment',
    description: 'Halal-segregated cold chain logistics from Southeast Asia to GCC and China. Full audit trail and GPS tracking included.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-009',
    name: 'Halal Packaging Solutions',
    supplier: 'GreenPack Halal Sdn Bhd',
    country: 'Malaysia',
    flag: '🇲🇾',
    category: 'Packaging',
    certStatus: 'Expiring Soon',
    certBody: 'JAKIM Recognized',
    certId: 'HC-MY-2025-008821',
    trustScore: 82,
    exportReady: ['Indonesia', 'GCC'],
    moq: '10,000 units',
    description: 'Biodegradable halal-compliant food packaging. Ink and adhesives verified halal. Certification renewal in progress.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-010',
    name: 'Halal Agriculture Inputs',
    supplier: 'Agri Tayyib Bahrain',
    country: 'Bahrain',
    flag: '🇧🇭',
    category: 'Agriculture',
    certStatus: 'Valid',
    certBody: 'BSMS Certified',
    certId: 'HC-BH-2026-000812',
    trustScore: 88,
    exportReady: ['GCC', 'Malaysia', 'OIC Markets'],
    moq: '2,000 kg',
    description: 'Certified halal agricultural inputs including bio-fertilizers and organic pesticides for halal farming operations.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-011',
    name: 'Halal ERP & Compliance SaaS',
    supplier: 'HalalTech Solutions',
    country: 'Malaysia',
    flag: '🇲🇾',
    category: 'Technology Services',
    certStatus: 'Valid',
    certBody: 'MDEC Verified',
    certId: 'HC-MY-2026-009912',
    trustScore: 90,
    exportReady: ['Indonesia', 'GCC', 'OIC Markets'],
    moq: 'Per licence',
    description: 'Cloud-based ERP and compliance management platform built for halal food manufacturers and certifiers.',
    image: null,
    shortlisted: false,
  },
  {
    id: 'prod-012',
    name: 'Halal Dairy & Cheese Range',
    supplier: 'Al-Nour Dairy UAE',
    country: 'UAE',
    flag: '🇦🇪',
    category: 'Halal Food & Beverage',
    certStatus: 'Valid',
    certBody: 'ESMA Certified',
    certId: 'HC-AE-2026-004401',
    trustScore: 95,
    exportReady: ['Malaysia', 'Indonesia', 'GCC', 'OIC Markets'],
    moq: '200 kg',
    description: 'Premium halal dairy and artisan cheese range certified by ESMA. Distributed across 12 OIC markets.',
    image: null,
    shortlisted: false,
  },
];

type Product = typeof products[0];

export default function MarketplaceClient() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedCountry, setSelectedCountry] = useState('All Countries');
  const [selectedCertStatus, setSelectedCertStatus] = useState('All Status');
  const [minTrust, setMinTrust] = useState(0);
  const [shortlisted, setShortlisted] = useState<Set<string>>(new Set());
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [filterOpen, setFilterOpen] = useState(true);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.supplier.toLowerCase().includes(search.toLowerCase());
      const matchCat = selectedCategory === 'All Categories' || p.category === selectedCategory;
      const matchCountry = selectedCountry === 'All Countries' || p.country === selectedCountry;
      const matchCert = selectedCertStatus === 'All Status' || p.certStatus === selectedCertStatus;
      const matchTrust = p.trustScore >= minTrust;
      return matchSearch && matchCat && matchCountry && matchCert && matchTrust;
    });
  }, [search, selectedCategory, selectedCountry, selectedCertStatus, minTrust]);

  const toggleShortlist = (id: string) => {
    setShortlisted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        toast.success('Removed from shortlist');
      } else {
        next.add(id);
        toast.success('Supplier added to shortlist');
      }
      return next;
    });
  };

  const certBadgeVariant = (status: string) => {
    if (status === 'Valid') return 'valid';
    if (status === 'Expiring Soon') return 'expiring';
    if (status === 'Under Review') return 'review';
    return 'pending';
  };

  return (
    <div className="max-w-screen-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Global Halal Marketplace</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
            {filtered.length} verified suppliers across 42+ countries
          </p>
        </div>
        <button
          onClick={() => setFilterOpen(!filterOpen)}
          className="btn-outline flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
        >
          <SlidersHorizontal size={15} />
          {filterOpen ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      <div className="flex gap-6">
        {/* Filter Panel */}
        {filterOpen && (
          <aside
            className="w-60 xl:w-72 shrink-0 rounded-2xl p-5 shadow-card self-start sticky top-6 space-y-5"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Search
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Product or supplier..."
                  className="w-full pl-8 pr-3 py-2 rounded-lg text-sm input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm input-field"
              >
                {categories.map((c) => <option key={`cat-${c}`} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm input-field"
              >
                {countries.map((c) => <option key={`country-${c}`} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Certificate Status
              </label>
              <div className="space-y-2">
                {certStatuses.map((s) => (
                  <label key={`certstatus-${s}`} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="certStatus"
                      value={s}
                      checked={selectedCertStatus === s}
                      onChange={() => setSelectedCertStatus(s)}
                      className="accent-primary"
                    />
                    <span className="text-sm" style={{ color: 'var(--foreground)' }}>{s}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Min Trust Score: <span style={{ color: 'var(--emerald-bright)' }}>{minTrust}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={minTrust}
                onChange={(e) => setMinTrust(Number(e.target.value))}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                <span>0%</span><span>100%</span>
              </div>
            </div>

            <button
              onClick={() => { setSearch(''); setSelectedCategory('All Categories'); setSelectedCountry('All Countries'); setSelectedCertStatus('All Status'); setMinTrust(0); }}
              className="w-full btn-outline py-2 rounded-lg text-sm flex items-center justify-center gap-2"
            >
              <X size={13} /> Clear Filters
            </button>
          </aside>
        )}

        {/* Product Grid */}
        <div className="flex-1 min-w-0">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <ShoppingBagIcon />
              <p className="text-base font-semibold mt-4 mb-2">No products match your filters</p>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Try adjusting your search criteria or clearing filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
              {filtered.map((product) => (
                <div
                  key={product.id}
                  className="rounded-2xl shadow-card transition-all duration-200 hover:-translate-y-1 flex flex-col overflow-hidden group"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                >
                  {/* Card Image Area */}
                  <div
                    className="h-36 flex items-center justify-center relative"
                    style={{ background: 'var(--muted)', borderBottom: '1px solid var(--border)' }}
                  >
                    <div className="text-5xl">{product.flag}</div>
                    <button
                      onClick={() => toggleShortlist(product.id)}
                      className="absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style={{
                        background: shortlisted.has(product.id) ? 'rgba(239,68,68,0.15)' : 'rgba(15,34,53,0.8)',
                        border: `1px solid ${shortlisted.has(product.id) ? 'rgba(239,68,68,0.4)' : 'var(--border)'}`,
                      }}
                      title={shortlisted.has(product.id) ? 'Remove from shortlist' : 'Add to shortlist'}
                    >
                      <Heart
                        size={14}
                        style={{ color: shortlisted.has(product.id) ? 'var(--danger)' : 'var(--muted-foreground)' }}
                        fill={shortlisted.has(product.id) ? 'var(--danger)' : 'none'}
                      />
                    </button>
                    <div className="absolute top-3 left-3">
                      <StatusBadge variant={certBadgeVariant(product.certStatus) as 'valid' | 'expiring' | 'review' | 'pending'} label={product.certStatus} />
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 flex flex-col flex-1">
                    <p className="text-xs mb-1 font-medium" style={{ color: 'var(--muted-foreground)' }}>{product.category}</p>
                    <h3 className="text-sm font-semibold mb-1 leading-snug">{product.name}</h3>
                    <div className="flex items-center gap-1.5 mb-3">
                      <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{product.supplier}</span>
                    </div>

                    {/* Trust Score */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <Star size={12} style={{ color: 'var(--gold)' }} fill="var(--gold)" />
                        <span className="text-xs font-semibold font-mono-data" style={{ color: 'var(--gold)' }}>
                          {product.trustScore}% Trust
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Award size={11} style={{ color: 'var(--emerald-bright)' }} />
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{product.certBody}</span>
                      </div>
                    </div>

                    {/* Export Ready */}
                    <div className="mb-4">
                      <p className="text-xs mb-1.5 font-medium" style={{ color: 'var(--muted-foreground)' }}>Export Ready:</p>
                      <div className="flex flex-wrap gap-1">
                        {product.exportReady.map((market) => (
                          <span
                            key={`export-${product.id}-${market}`}
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ background: 'rgba(10,107,79,0.12)', color: 'var(--emerald-bright)', border: '1px solid rgba(10,107,79,0.2)' }}
                          >
                            {market}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-auto">
                      <button
                        onClick={() => setSelectedProduct(product)}
                        className="flex-1 btn-primary py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
                      >
                        <Globe size={12} />
                        View Details
                      </button>
                      <button
                        onClick={() => toast.success('Quotation request sent to supplier')}
                        className="flex-1 btn-outline py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5"
                      >
                        <MessageSquare size={12} />
                        Request Quote
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}

function ShoppingBagIcon() {
  return (
    <div
      className="w-16 h-16 rounded-2xl flex items-center justify-center"
      style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
    >
      <Search size={28} style={{ color: 'var(--muted-foreground)' }} />
    </div>
  );
}